import type { ActionNames, ChildGuideItem } from '@tunein/web-api-client';
import type { ComponentType, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { selectIsMobile } from 'src/common/selectors/app';
import {
  type GuideItemSwipeActionComponentProps,
  RemoveRecentSwipeAction,
} from './RemoveRecentSwipeAction';

interface GuideItemSwipeActionProps {
  guideItem?: ChildGuideItem;
  children: ReactElement;
}

interface SwipeActionProps extends GuideItemSwipeActionComponentProps {
  actionName: ActionNames;
}

// Actions the API can hang off an item's `swipe` behavior. Add an entry here to support a new one.
const swipeActionComponents: Partial<
  Record<ActionNames, ComponentType<GuideItemSwipeActionComponentProps>>
> = {
  RemoveRecent: RemoveRecentSwipeAction,
};

/**
 * Restricted to mobile clients on purpose: a swipe is a touch gesture, and the same action is
 * already reachable on pointer clients through the hover-revealed row actions (see GuideItemActions,
 * which shipped for desktop in TUNE-18982).
 */
function SwipeAction({ actionName, guideItem, children }: SwipeActionProps) {
  const isMobile = useSelector(selectIsMobile);

  if (!isMobile) {
    return children;
  }

  const Action = swipeActionComponents[
    actionName
  ] as ComponentType<GuideItemSwipeActionComponentProps>;

  return <Action guideItem={guideItem}>{children}</Action>;
}

/**
 * Wraps a guide item's row so swiping it towards the trailing edge runs the action the API assigned
 * to that gesture — the touch counterpart to the hover-revealed row actions, and how a single item
 * gets removed from the Recents page on a phone.
 *
 * Which action a swipe runs is decided entirely by `behaviors.swipe`, so there's no client-side
 * allowlist to keep in sync with the API. Deliberately hook-free: it wraps every row in every list,
 * so it returns `children` untouched before anything that reads the store can run.
 */
export function GuideItemSwipeAction({ guideItem, children }: GuideItemSwipeActionProps) {
  const actionName = guideItem?.behaviors?.swipe?.actionName;

  if (!guideItem || !actionName || !swipeActionComponents[actionName]) {
    return children;
  }

  return (
    <SwipeAction actionName={actionName} guideItem={guideItem}>
      {children}
    </SwipeAction>
  );
}
